import { useEffect } from 'react';
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { waitForAuthHydration, useAuthStore } from '@/store/auth-store';
import { getUserInfo } from '@/lib/api/auth';
import { AppShell } from '@/components/layout/app-shell';
import type { NavPermissions } from '@/components/layout/sidebar';
import { useHasMenu, useUserInfo } from '@/lib/hooks/use-auth';

export const Route = createFileRoute('/_app')({
  beforeLoad: async ({ location }) => {
    await waitForAuthHydration();
    const state = useAuthStore.getState();
    if (!state.token) {
      // Keep path and search separate — restoring them requires distinct slots
      // because `navigate({ to })` expects a pathname, not a full URL.
      throw redirect({
        to: '/login',
        search: {
          redirectPath: location.pathname,
          redirectSearch: location.searchStr || undefined,
        },
      });
    }
    // menuList is not persisted to localStorage (security). On cold load or refresh the
    // store has an empty menuList even though the session is valid. Fetch userInfo here
    // so child route guards (requireMenuOrRedirect) see a populated menuList and don't
    // incorrectly treat the session as stale.
    if (state.menuList.length === 0) {
      try {
        const userInfo = await getUserInfo();
        const perms = userInfo.permissionList?.map((p) => p.permissionCode) ?? [];
        const menus = userInfo.menuList?.map((p) => p.permissionCode) ?? [];
        state.setSession(
          state.token,
          userInfo.username ?? state.username ?? undefined,
          perms,
          menus,
        );
      } catch {
        // If userInfo fetch fails the token may be expired — redirect to login
        throw redirect({
          to: '/login',
          search: {
            redirectPath: location.pathname,
            redirectSearch: location.searchStr || undefined,
          },
        });
      }
    }
  },
  component: AppLayout,
});

function AppLayout() {
  // permissionList/menuList are not persisted (see auth-store partialize) — re-hydrate
  // from /auth/user-info on every mount so refresh restores the menu/permission set.
  const { data: userInfo } = useUserInfo();
  const setSession = useAuthStore((s) => s.setSession);
  const token = useAuthStore((s) => s.token);
  const username = useAuthStore((s) => s.username);
  useEffect(() => {
    if (userInfo && token) {
      const perms = userInfo.permissionList?.map((p) => p.permissionCode) ?? [];
      const menus = userInfo.menuList?.map((p) => p.permissionCode) ?? [];
      setSession(token, userInfo.username ?? username ?? undefined, perms, menus);
    }
  }, [userInfo, token, username, setSession]);

  const nav: NavPermissions = {
    dashboard: useHasMenu('menu:ads-admin:dashboard'),
    accounts: useHasMenu('menu:ads-admin:ad-accounts'),
    transactions: useHasMenu('menu:ads-admin:ad-account-transactions'),
    apps: useHasMenu('menu:ads-admin:apps'),
    operators: useHasMenu('menu:ads-admin:operators'),
    users: useHasMenu('menu:ads-admin:users'),
    permissions: useHasMenu('menu:ads-admin:permissions'),
    platforms: useHasMenu('menu:ads-admin:ad-platforms'),
    providers: useHasMenu('menu:ads-admin:ad-providers'),
    // 两页各占一个码而不是共用一个，是为了以后能单独放开其中一页
    materialsReview: useHasMenu('menu:ads-admin:material-review'),
    materialsDisplay: useHasMenu('menu:ads-admin:material-display'),
    materialProcess: useHasMenu('menu:ads-admin:material-process'),
    auditLogs: useHasMenu('menu:ads-admin:audit-log'),
  };

  return (
    <AppShell nav={nav}>
      <Outlet />
    </AppShell>
  );
}
